import React, { useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/auth";
import FinanceLayout, { fmtUsd } from "./FinanceLayout";
import { Loader2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const ForecastPage = () => {
  const [months, setMonths] = useState(36);
  const [growth, setGrowth] = useState(0.10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/finance/forecast?months=${months}&growth_rate=${growth}`);
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [months, growth]);

  return (
    <FinanceLayout>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <TrendingUp size={20} className="text-nex-gold" />
          <div className="mono-label">// STRATEGIC OUTLOOK</div>
        </div>
        <h1 className="serif-head text-3xl">Revenue Forecast</h1>
        <p className="text-nex-chrome mt-2 text-sm">
          Baseline pulled from your last 90 days of retainer + deployment revenue. Adjust the growth rate to model scenarios.
        </p>
      </div>

      <div className="card-industrial corner-tick p-5 mb-6 grid md:grid-cols-4 gap-4" data-testid="forecast-controls">
        <div>
          <div className="mono-label !text-[9px] mb-2">Horizon</div>
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="input-industrial" data-testid="months-select">
            <option value={12}>12 months</option>
            <option value={24}>24 months</option>
            <option value={36}>36 months (3 years)</option>
            <option value={60}>60 months (5 years)</option>
          </select>
        </div>
        <div>
          <div className="mono-label !text-[9px] mb-2">Annual Growth</div>
          <select value={growth} onChange={(e) => setGrowth(Number(e.target.value))} className="input-industrial" data-testid="growth-select">
            <option value={0.05}>Conservative (5%)</option>
            <option value={0.10}>Realistic (10%)</option>
            <option value={0.15}>Aggressive (15%)</option>
            <option value={0.25}>Hyper-growth (25%)</option>
          </select>
        </div>
        {data && (
          <>
            <div>
              <div className="mono-label !text-[9px] mb-2">Baseline Monthly</div>
              <div className="serif-head text-xl text-nex-gold">{fmtUsd(data.baseline_monthly)}</div>
              <div className="mono-body text-[10px] text-nex-muted">
                Retainers: {fmtUsd(data.baseline_retainer_monthly)}/mo · Deployments: {fmtUsd(data.baseline_deployment_monthly)}/mo
              </div>
            </div>
            <div>
              <div className="mono-label !text-[9px] mb-2">Projected Year 3</div>
              <div className="serif-head text-xl text-nex-silver">
                {fmtUsd(data.series[data.series.length - 1]?.revenue)}<span className="text-nex-muted text-sm"> / mo</span>
              </div>
              <div className="mono-body text-[10px] text-nex-muted">
                Net: {fmtUsd(data.series[data.series.length - 1]?.net)}
              </div>
            </div>
          </>
        )}
      </div>

      {loading || !data ? (
        <div className="text-center py-20"><Loader2 className="animate-spin text-nex-gold mx-auto" /></div>
      ) : data.baseline_monthly === 0 ? (
        <div className="card-industrial corner-tick p-10 text-center" data-testid="forecast-empty">
          <TrendingUp className="text-nex-muted mx-auto mb-3" size={28} />
          <h2 className="serif-head text-xl mb-2">No baseline yet</h2>
          <p className="text-nex-chrome text-sm">
            Once your first retainer or deployment lands in the transaction ledger, the 3-year forecast will populate automatically.
          </p>
        </div>
      ) : (
        <>
          <div className="card-industrial corner-tick p-5 mb-6" data-testid="forecast-chart">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.series} margin={{ top: 8, right: 24, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                  <XAxis dataKey="month" stroke="#64748B" fontSize={10} />
                  <YAxis stroke="#64748B" fontSize={10} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: "#0F172A", border: "1px solid #334155", borderRadius: 0 }}
                    formatter={(v) => fmtUsd(v)}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#D4AF37" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="net" name="Net Distributable" stroke="#34D399" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card-industrial corner-tick overflow-hidden" data-testid="forecast-table">
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="bg-black/30 border-b border-nex-border sticky top-0">
                  <tr className="mono-body text-[10px] uppercase tracking-[0.15em] text-nex-chrome">
                    <th className="text-left px-4 py-3">Month</th>
                    <th className="text-right px-4 py-3">Revenue</th>
                    <th className="text-right px-4 py-3">COGS</th>
                    <th className="text-right px-4 py-3">Reserve</th>
                    <th className="text-right px-4 py-3">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {data.series.map((row) => (
                    <tr key={row.month} className="border-b border-nex-border/50">
                      <td className="px-4 py-2 mono-body text-[11px] text-nex-chrome">{row.month}</td>
                      <td className="px-4 py-2 text-right text-nex-silver">{fmtUsd(row.revenue)}</td>
                      <td className="px-4 py-2 text-right text-nex-chrome">{fmtUsd(row.cogs)}</td>
                      <td className="px-4 py-2 text-right text-nex-chrome">{fmtUsd(row.reserve)}</td>
                      <td className="px-4 py-2 text-right text-nex-gold font-medium">{fmtUsd(row.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </FinanceLayout>
  );
};

export default ForecastPage;
